import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost, useFetchResource } from '@app/src/api/fetchHooks'
import type RequestError from '@app/src/errors/RequestError'
import useApi from '@app/src/hooks/api'
import useErrorNotification from '@app/src/hooks/errorNotification'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { buildProviderSearchPayload } from '@app/src/pages/TabularImport/shared/providerSearch'
import { useTabularImportEntity } from '@app/src/pages/TabularImport/shared/TabularImportEntityContext'
import type {
  DeduplicationSummary,
  DeduplicationUserAction,
  ImportRecord,
  ImportSession,
  PaginatedDeduplicationResults,
  PaginatedImportRecords,
  StartImportRequest,
  StartImportResponse,
  TabularImportInitResponse,
  UnfinishedImportSession,
  UpdateMappingsRequest,
  UpdateRecordRequest,
} from '@app/src/types/tabularImport'
import { isProcessingStatus } from '@app/src/types/tabularImport'
import type { UseMutationOptions } from '@tanstack/react-query'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

export const TabularImportQueryKey = {
  base: ['tabular-import'] as const,
  status: (sessionId: string | null) => [FetchKey.TabularImportStatus, sessionId] as const,
  records: (sessionId: string | null) => ['tabular-import', 'records', sessionId] as const,
  unfinishedSessions: (entityType: string) => ['tabular-import-unfinished-sessions', entityType] as const,
  deduplicationResults: (sessionId: string | null) => ['tabular-import', sessionId, 'deduplication-results'] as const,
  deduplicationSummary: (sessionId: string | null) => ['tabular-import', sessionId, 'deduplication-summary'] as const,
}

export function invalidateDedup(queryClient: ReturnType<typeof useQueryClient>, sessionId: string | null): void {
  queryClient.invalidateQueries({ queryKey: TabularImportQueryKey.deduplicationResults(sessionId) })
  queryClient.invalidateQueries({ queryKey: TabularImportQueryKey.status(sessionId) })
  queryClient.invalidateQueries({ queryKey: TabularImportQueryKey.deduplicationSummary(sessionId) })
}

export const useUploadTabularImport = (
  options?: Omit<UseMutationOptions<TabularImportInitResponse, RequestError, File>, 'mutationFn'>,
) => {
  const { entityType } = useTabularImportEntity()
  const { post } = useApi()
  const queryClient = useQueryClient()
  const { showErrorNotification } = useErrorNotification()

  return useMutation({
    ...options,
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      return post<TabularImportInitResponse, FormData>({
        url: endpoints.tabularImportInitiate(entityType),
        body: formData,
      })
    },
    onSuccess: (...args) => {
      // Invalidate unfinished sessions list when a new import starts
      queryClient.invalidateQueries({ queryKey: TabularImportQueryKey.unfinishedSessions(entityType) })
      options?.onSuccess?.(...args)
    },
    onError: (...args) => {
      showErrorNotification({ requestError: args[0] })
      options?.onError?.(...args)
    },
  })
}

export const useUnfinishedTabularImportSessions = () => {
  const { entityType } = useTabularImportEntity()
  const { get } = useApi()

  return useQuery({
    queryKey: TabularImportQueryKey.unfinishedSessions(entityType),
    queryFn: async () => {
      return get<UnfinishedImportSession[]>({ url: endpoints.tabularImportSessions(entityType) })
    },
  })
}

const ANALYZING_TIMEOUT_MS = 10 * 60 * 1000

function isAnalyzingTimedOut(startTime: number | null): boolean {
  return startTime !== null && Date.now() - startTime > ANALYZING_TIMEOUT_MS
}

export const useTabularImportStatus = (sessionId: string | null) => {
  const { entityType } = useTabularImportEntity()
  const analyzingStartRef = useRef<number | null>(null)

  const query = useFetchResource<ImportSession>({
    key: [FetchKey.TabularImportStatus, sessionId ?? ''],
    endpoint: endpoints.tabularImportStatus(entityType, sessionId ?? ''),
    shouldShowErrorNotification: false,
    options: {
      enabled: Boolean(sessionId),
      refetchInterval: query => {
        const data = query.state.data
        if (!data || !isProcessingStatus(data.status)) return false
        if (data.status === 'Analyzing' && isAnalyzingTimedOut(analyzingStartRef.current)) return false
        return 2000
      },
    },
  })

  // Track when analyzing started
  useEffect(() => {
    if (query.data?.status === 'Analyzing') {
      if (analyzingStartRef.current === null) {
        analyzingStartRef.current = Date.now()
      }
    } else {
      analyzingStartRef.current = null
    }
  }, [query.data?.status])

  // Override status to Failed when analyzing has timed out
  const data = deriveTimedOutStatus(query.data, analyzingStartRef.current)

  return { ...query, data }
}

function deriveTimedOutStatus(
  data: ImportSession | undefined,
  analyzingStart: number | null,
): ImportSession | undefined {
  if (!data) return undefined
  if (data.status === 'Analyzing' && isAnalyzingTimedOut(analyzingStart)) {
    return { ...data, status: 'Failed' as const, errorMessage: 'Analysis timed out. Please try again.' }
  }
  return data
}

export const useTabularImportRecords = (
  sessionId: string | null,
  page: number,
  pageSize: number,
  status?: string,
  search?: string,
) => {
  const { entityType } = useTabularImportEntity()
  const { post } = useApi()

  return useQuery({
    queryKey: [...TabularImportQueryKey.records(sessionId), sessionId, page, pageSize, status, search] as const,
    queryFn: async () => {
      return post<
        PaginatedImportRecords,
        { pagination: { pageNumber: number; itemsPerPage: number }; status?: string; search?: string }
      >({
        url: endpoints.tabularImportRecords(entityType, sessionId ?? ''),
        body: {
          pagination: { pageNumber: page, itemsPerPage: pageSize },
          status: status || undefined,
          search: search || undefined,
        },
      })
    },
    enabled: Boolean(sessionId),
    placeholderData: previousData => previousData,
  })
}

// =====================================================
// Tabular Import Mutations
// =====================================================

export interface UpdateRecordParams {
  sessionId: string
  rowNumber: number
  extractedFields: Record<string, string>
  resolvedIds?: Record<string, number | null>
}

export const useUpdateTabularRecord = (
  options?: Omit<UseMutationOptions<ImportRecord, RequestError, UpdateRecordParams>, 'mutationFn'>,
) => {
  const { entityType } = useTabularImportEntity()
  const { put } = useApi()
  const queryClient = useQueryClient()
  const { showErrorNotification } = useErrorNotification()

  return useMutation({
    ...options,
    mutationFn: async ({ sessionId, rowNumber, extractedFields, resolvedIds }: UpdateRecordParams) => {
      return put<ImportRecord, UpdateRecordRequest>({
        url: endpoints.tabularImportRecord(entityType, sessionId, rowNumber),
        body: { extractedFields, resolvedIds },
      })
    },
    onSuccess: (data, vars, ...rest) => {
      queryClient.invalidateQueries({ queryKey: TabularImportQueryKey.status(vars.sessionId) })
      queryClient.invalidateQueries({ queryKey: TabularImportQueryKey.records(vars.sessionId) })
      options?.onSuccess?.(data, vars, ...rest)
    },
    onError: (...args) => {
      showErrorNotification({ requestError: args[0] })
      options?.onError?.(...args)
    },
  })
}

export interface SetStatusParams {
  sessionId: string
  status: string
}

export const useSetTabularImportStatus = (
  options?: Omit<UseMutationOptions<{ message: string }, RequestError, SetStatusParams>, 'mutationFn'>,
) => {
  const { entityType } = useTabularImportEntity()
  const { post } = useApi()
  const queryClient = useQueryClient()
  const { showErrorNotification } = useErrorNotification()

  return useMutation({
    mutationFn: ({ sessionId, status }: SetStatusParams) =>
      post<{ message: string }, { status: string }>({
        url: endpoints.tabularImportSetStatus(entityType, sessionId),
        body: { status },
      }),
    onSuccess: (_, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: TabularImportQueryKey.status(sessionId) })
      queryClient.invalidateQueries({ queryKey: TabularImportQueryKey.deduplicationResults(sessionId) })
    },
    ...options,
    onError: (...args) => {
      showErrorNotification({ requestError: args[0] })
      options?.onError?.(...args)
    },
  })
}

export interface StartImportParams {
  sessionId: string
  rowNumbersToImport?: number[]
}

export const useStartTabularImport = (
  options?: Omit<UseMutationOptions<StartImportResponse, RequestError, StartImportParams>, 'mutationFn'>,
) => {
  const { entityType } = useTabularImportEntity()
  const { post } = useApi()
  const queryClient = useQueryClient()
  const { showErrorNotification } = useErrorNotification()

  return useMutation({
    mutationFn: ({ sessionId, rowNumbersToImport }: StartImportParams) =>
      post<StartImportResponse, StartImportRequest>({
        url: endpoints.tabularImportImport(entityType, sessionId),
        body: rowNumbersToImport ? { rowNumbersToImport } : {},
      }),
    onSuccess: (_, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: TabularImportQueryKey.status(sessionId) })
    },
    ...options,
    onError: (...args) => {
      showErrorNotification({ requestError: args[0] })
      options?.onError?.(...args)
    },
  })
}

export const useStartDeduplication = (options?: { onSuccess?: () => void }) => {
  const { entityType } = useTabularImportEntity()
  const { post } = useApi()
  const { showErrorNotification } = useErrorNotification()

  return useMutation({
    mutationFn: (sessionId: string) =>
      post<{ message: string }>({ url: endpoints.tabularImportReady(entityType, sessionId) }),
    onSuccess: () => options?.onSuccess?.(),
    onError: (error: RequestError) => showErrorNotification({ requestError: error }),
  })
}

export interface UpdateMappingsParams {
  sessionId: string
  mappings: { id: number; isApplied: boolean }[]
}

export const useUpdateTabularMappings = (
  options?: Omit<UseMutationOptions<ImportSession, RequestError, UpdateMappingsParams>, 'mutationFn'>,
) => {
  const { entityType } = useTabularImportEntity()
  const { put } = useApi()
  const queryClient = useQueryClient()
  const { showErrorNotification } = useErrorNotification()
  return useMutation({
    mutationFn: ({ sessionId, mappings }: UpdateMappingsParams) =>
      put<ImportSession, UpdateMappingsRequest>({
        url: endpoints.tabularImportMappings(entityType, sessionId),
        body: { mappings },
      }),
    onSuccess: (data, { sessionId }) => {
      queryClient.setQueryData(TabularImportQueryKey.status(sessionId), data)
    },
    ...options,
    onError: (...args) => {
      showErrorNotification({ requestError: args[0] })
      options?.onError?.(...args)
    },
  })
}

export const useApproveCurrentMappings = (
  options?: Omit<UseMutationOptions<{ message: string }, RequestError, string>, 'mutationFn'>,
) => {
  const { entityType } = useTabularImportEntity()
  const { post } = useApi()
  const queryClient = useQueryClient()
  const { showErrorNotification } = useErrorNotification()
  return useMutation({
    mutationFn: (sessionId: string) =>
      post<{ message: string }>({
        url: endpoints.tabularImportApproveMappings(entityType, sessionId),
      }),
    onSuccess: (_, sessionId) => {
      queryClient.invalidateQueries({ queryKey: TabularImportQueryKey.status(sessionId) })
    },
    ...options,
    onError: (...args) => {
      showErrorNotification({ requestError: args[0] })
      options?.onError?.(...args)
    },
  })
}

// =====================================================
// Deduplication Hooks
// =====================================================

export const useDeduplicationSummary = (sessionId: string | null, options?: { refetchInterval?: number | false }) => {
  const { entityType } = useTabularImportEntity()
  const { get } = useApi()

  return useQuery({
    queryKey: TabularImportQueryKey.deduplicationSummary(sessionId),
    queryFn: () =>
      get<DeduplicationSummary>({
        url: endpoints.tabularImportDeduplicationSummary(entityType, sessionId ?? ''),
      }),
    enabled: Boolean(sessionId),
    refetchInterval: options?.refetchInterval,
  })
}

export const usePagedDeduplicationResults = (
  sessionId: string | null,
  params: {
    action: DeduplicationUserAction
    page: number
    pageSize: number
    search?: string
  },
  enabled = true,
) => {
  const { entityType } = useTabularImportEntity()
  const { get } = useApi()

  return useQuery({
    queryKey: [
      ...TabularImportQueryKey.deduplicationResults(sessionId),
      sessionId,
      params.action,
      params.page,
      params.pageSize,
      params.search,
    ],
    queryFn: async () => {
      const searchParams = new URLSearchParams({
        action: params.action,
        page: params.page.toString(),
        pageSize: params.pageSize.toString(),
      })
      if (params.search) searchParams.set('search', params.search)

      return get<PaginatedDeduplicationResults>({
        url: endpoints.tabularImportDeduplicationResults(entityType, sessionId ?? '', searchParams.toString()),
      })
    },
    enabled: Boolean(sessionId) && enabled,
  })
}

export const useBulkDeduplicationAction = (sessionId: string | null) => {
  const { entityType } = useTabularImportEntity()
  const { put } = useApi()
  const queryClient = useQueryClient()
  const { showErrorNotification } = useErrorNotification()

  return useMutation({
    mutationFn: (body: {
      currentAction: DeduplicationUserAction
      newAction: DeduplicationUserAction
      ids: number[] | null
    }) =>
      put<{ affectedCount: number }, typeof body>({
        url: endpoints.tabularImportDeduplicationBulkAction(entityType, sessionId ?? ''),
        body,
      }),
    onSuccess: () => invalidateDedup(queryClient, sessionId),
    onError: (error: RequestError) => showErrorNotification({ requestError: error }),
  })
}

export interface UpdateDeduplicationDecisionParams {
  id: number
  action: DeduplicationUserAction
  fieldDecisions?: Record<string, string>
}

export const useUpdateDeduplicationDecision = (
  sessionId: string | null,
  options?: Omit<UseMutationOptions<void, RequestError, UpdateDeduplicationDecisionParams>, 'mutationFn'>,
) => {
  const { entityType } = useTabularImportEntity()
  const { put } = useApi()
  const queryClient = useQueryClient()
  const { showErrorNotification } = useErrorNotification()

  return useMutation({
    ...options,
    mutationFn: ({ id, action, fieldDecisions }: UpdateDeduplicationDecisionParams) =>
      put<void, { action: DeduplicationUserAction; fieldDecisions?: Record<string, string> }>({
        url: endpoints.tabularImportDeduplicationDecision(entityType, id),
        body: { action, fieldDecisions },
      }),
    onSuccess: (...args) => {
      invalidateDedup(queryClient, sessionId)
      options?.onSuccess?.(...args)
    },
    onError: (...args) => {
      showErrorNotification({ requestError: args[0] })
      options?.onError?.(...args)
    },
  })
}

export interface AssignEntityParams {
  id: number
  entityId: number
}

export const useAssignEntity = (sessionId: string | null) => {
  const { entityType } = useTabularImportEntity()
  const { put } = useApi()
  const queryClient = useQueryClient()
  const { showErrorNotification } = useErrorNotification()

  return useMutation<void, RequestError, AssignEntityParams>({
    mutationFn: ({ id, entityId }) =>
      put<void, { entityId: number }>({
        url: endpoints.tabularImportDeduplicationAssign(entityType, id),
        body: { entityId },
      }),
    onSuccess: () => invalidateDedup(queryClient, sessionId),
    onError: (error: RequestError) => showErrorNotification({ requestError: error }),
  })
}

export const useRemoveAssignment = (sessionId: string | null) => {
  const { entityType } = useTabularImportEntity()
  const api = useApi()
  const queryClient = useQueryClient()
  const { showErrorNotification } = useErrorNotification()

  return useMutation<void, RequestError, number>({
    mutationFn: (dedupResultId: number) =>
      api.delete<void, never>({
        url: endpoints.tabularImportDeduplicationAssign(entityType, dedupResultId),
      }),
    onSuccess: () => invalidateDedup(queryClient, sessionId),
    onError: (error: RequestError) => showErrorNotification({ requestError: error }),
  })
}

export type ProviderSearchResult = { id: number; name: string; country?: { name: string } | null }

export const useSearchProviders = (query: string, enabled: boolean) =>
  useFetchCollectionWithPost<ProviderSearchResult>({
    key: [FetchKey.ProviderCollection, query],
    endpoint: endpoints.providersCollection,
    payload: buildProviderSearchPayload(query),
    options: { enabled },
    // A type-ahead fires per keystroke; a snackbar per failed request would bury the UI.
    shouldShowErrorNotification: false,
  })

export type CountryResult = { id: number; name: string; countryCode: string }

export const useSearchCountries = (query: string, enabled: boolean) =>
  useFetchCollectionWithPost<CountryResult>({
    key: [FetchKey.Country, query],
    endpoint: endpoints.countries,
    payload: {
      filter: [{ name: 'name', filters: [{ operator: Operators.Contains, value: query }] }],
      pagination: { pageNumber: 1, itemsPerPage: 20 },
      include: [],
    },
    options: { enabled: enabled && query.length >= 1 },
    // A type-ahead fires per keystroke; a snackbar per failed request would bury the UI.
    shouldShowErrorNotification: false,
  })

// =====================================================
// Mapping overrides (question the AI's column guess)
// =====================================================

type RetargetMappingParams = { sessionId: string; mappingId: number; targetField: string }

export const useRetargetMapping = () => {
  const { entityType } = useTabularImportEntity()
  const { put } = useApi()
  const queryClient = useQueryClient()
  const { showErrorNotification } = useErrorNotification()

  return useMutation({
    mutationFn: ({ sessionId, mappingId, targetField }: RetargetMappingParams) =>
      put<ImportSession, { targetField: string }>({
        url: endpoints.tabularImportMappingTarget(entityType, sessionId, mappingId),
        body: { targetField },
      }),
    onSuccess: (data, { sessionId }) => {
      queryClient.setQueryData(TabularImportQueryKey.status(sessionId), data)
    },
    // These overrides reject with a specific, actionable message (e.g. which column or
    // limit). Surface it instead of the generic toast — there's no errorCode to map.
    onError: (error: RequestError) =>
      showErrorNotification({ requestError: error, specificFallbackErrorMessage: error.body?.message }),
  })
}

type SplitMappingParams = { sessionId: string; mappingId: number }

export const useSplitMapping = () => {
  const { entityType } = useTabularImportEntity()
  const { post } = useApi()
  const queryClient = useQueryClient()
  const { showErrorNotification } = useErrorNotification()

  return useMutation({
    mutationFn: ({ sessionId, mappingId }: SplitMappingParams) =>
      post<ImportSession, Record<string, never>>({
        url: endpoints.tabularImportMappingSplit(entityType, sessionId, mappingId),
        body: {},
      }),
    onSuccess: (data, { sessionId }) => {
      queryClient.setQueryData(TabularImportQueryKey.status(sessionId), data)
    },
    onError: (error: RequestError) =>
      showErrorNotification({ requestError: error, specificFallbackErrorMessage: error.body?.message }),
  })
}
